import React, { useEffect, useState, createContext, useContext } from 'react';
import { components } from '@/api/types';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { createPageTitle } from '../../utils/title';
import styles from '@/assets/styles/UserInfo.module.less';

type UserInfoType = components['schemas']['User'];

// 创建 Context
const UserInfoContext = createContext<UserInfoType | null>(null);

// 导出 Hook 供 children 使用
export const useUserInfo = () => {
  const context = useContext(UserInfoContext);
  if (!context) {
    throw new Error('useUserInfo must be used within UserInfo component');
  }
  return context;
};

const UserInfo: React.FC<{
  title?: string;
  children?: React.ReactNode
}> = ( { title, children } ) => {
  const [userInfo, setUserInfo] = useState<UserInfoType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const storedUserInfo = localStorage.getItem('userInfo');
        if (!storedUserInfo) {
          console.log('No user info found, redirecting to login');
          navigate('/login');
          return;
        }

        const parsedInfo = JSON.parse(storedUserInfo);
        if (!parsedInfo || !parsedInfo.userId) {
          console.log('Invalid user info, redirecting to login');
          localStorage.removeItem('userInfo');
          navigate('/login');
          return;
        }

        setUserInfo(parsedInfo);
      } catch (error) {
        console.error('Error loading user info:', error);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserInfo();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>加载中...</div>
      </div>
    );
  }

  if (!userInfo) {
    return null;
  }

  return (
    <div className={styles.userLayout}>
      <Helmet>
        <title>{createPageTitle(title || '我的主页' + ' - Furiflow')}</title>
      </Helmet>
      <UserInfoContext.Provider value={userInfo}>
        <section className={styles.container}>{children}</section>
      </UserInfoContext.Provider>
    </div>
  );
};

export default UserInfo;
